import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { useUpdateLesson } from "../../../api/mutations";
import * as client from "../../../api/client";
import { useGetTopic } from "../../../api/queries";
import Button from "../../../componentLibrary/Button";
import Checkbox from "../../../componentLibrary/Checkbox";
import ConfirmationBox from "../../../componentLibrary/ConfirmationBox";
import Spinner from "../../../componentLibrary/Spinner";
import { SectionHeader } from "../../../componentLibrary/SectionPanel";
import { useNotification } from "../../../hooks/useNotification";
import { ErrorDisplay } from "../../../utils/ErrorHandling";
import {
  applyToolboxToolFlagsToLesson,
  getLessonsWithToolboxToolFlagChanges,
  getToolboxToolAdminSections,
  haveToolboxToolFlagsChanged,
  topicToolboxToolFlags,
} from "../../../utils/toolboxTools";
import type { ToolboxManagementLayoutContext } from "./Layout";

export default function ToolboxToolsPage() {
  const { topicId = "" } = useParams<{ topicId: string }>();
  const navigate = useNavigate();
  const { setViewLearnerToolboxHandler } =
    useOutletContext<ToolboxManagementLayoutContext>();
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { data: topic, isLoading, isError, error } = useGetTopic(topicId);
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [baseline, setBaseline] = useState<Record<string, boolean>>({});
  const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!topic?._id) return;
    const initial = topicToolboxToolFlags(topic);
    setFlags(initial);
    setBaseline({ ...initial });
  }, [topic?._id]);

  const isDirty = useMemo(
    () => haveToolboxToolFlagsChanged(flags, baseline),
    [flags, baseline],
  );

  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const backPath = `/admin/toolboxes/${topicId}`;
  const updateLessonMutation = useUpdateLesson({});

  const sections = useMemo(
    () => (topic ? getToolboxToolAdminSections(topic) : []),
    [topic],
  );

  const allPageIds = useMemo(
    () => sections.flatMap(({ rows }) => rows.map(({ page }) => page._id)),
    [sections],
  );

  const areAllTopicPagesChecked =
    allPageIds.length > 0 &&
    allPageIds.every((pageId) => Boolean(flags[pageId]));

  const handleToggle = useCallback((pageId: string) => {
    setFlags((prev) => ({ ...prev, [pageId]: !prev[pageId] }));
  }, []);

  const setCheckedStateForPages = useCallback(
    (pageIds: string[], checked: boolean) => {
      setFlags((prev) => {
        const next = { ...prev };
        for (const pageId of pageIds) {
          next[pageId] = checked;
        }
        return next;
      });
    },
    [],
  );

  const handleToggleTopicPages = useCallback(() => {
    setCheckedStateForPages(allPageIds, !areAllTopicPagesChecked);
  }, [allPageIds, areAllTopicPagesChecked, setCheckedStateForPages]);

  const attemptNavigate = useCallback(
    (path: string) => {
      if (!isDirty) {
        navigate(path);
        return;
      }
      setPendingNavigationPath(path);
      setLeaveConfirmOpen(true);
    },
    [isDirty, navigate],
  );

  useEffect(() => {
    const learnerToolboxPath = `/toolboxes/${topicId}`;
    setViewLearnerToolboxHandler(() => () => {
      attemptNavigate(learnerToolboxPath);
    });

    return () => setViewLearnerToolboxHandler(null);
  }, [attemptNavigate, setViewLearnerToolboxHandler, topicId]);

  const handleSave = async (redirectPath: string = backPath) => {
    if (!topic?.lessons?.length) {
      navigate(redirectPath);
      return;
    }
    const lessonsToUpdate = getLessonsWithToolboxToolFlagChanges(
      topic,
      flags,
      baseline,
    );
    if (lessonsToUpdate.length === 0) {
      navigate(redirectPath);
      return;
    }
    try {
      for (const lesson of lessonsToUpdate) {
        const topicsContaining = await client.getLessonsContainedTopics(
          lesson._id!,
        );
        const moduleIds = topicsContaining
          .map((m) => m._id)
          .filter((id): id is string => Boolean(id));
        await updateLessonMutation.mutateAsync({
          lesson: applyToolboxToolFlagsToLesson(lesson, flags),
          modules: moduleIds,
        });
      }
      notify("success", "Tool settings saved");
      await queryClient.invalidateQueries({ queryKey: ["modules", topicId] });
      await queryClient.invalidateQueries({ queryKey: ["modules"] });
      setBaseline({ ...flags });
      setLeaveConfirmOpen(false);
      setPendingNavigationPath(null);
      navigate(redirectPath);
    } catch {
      notify("error", "Failed to save tool settings");
    }
  };

  const handleCancel = () => {
    attemptNavigate(backPath);
  };

  const handleDiscardChanges = () => {
    setLeaveConfirmOpen(false);
    const nextPath = pendingNavigationPath ?? backPath;
    setPendingNavigationPath(null);
    navigate(nextPath);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="w-8" />
      </div>
    );
  }

  if (isError) {
    return <ErrorDisplay error={error} messageSource="api" />;
  }

  if (!topic) {
    return (
      <div className="pt-4">
        <p className="text-sm text-gray-600">Topic not found.</p>
        <button
          type="button"
          onClick={() => navigate("/admin/toolboxes")}
          className="mt-2 text-sm font-medium text-electrum-300 hover:text-electrum"
        >
          Back to topic list
        </button>
      </div>
    );
  }

  const topicTitle = topic.title;

  return (
    <div className="pt-4">
      <div className="mb-4">
        <button
          type="button"
          onClick={handleCancel}
          className="flex items-center gap-1.5 text-sm text-electrum-300 hover:text-electrum transition-colors mb-4 w-fit"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Toolbox Manager
        </button>
        <div className="flex items-center gap-3">
          {topic.icon && (
            <img
              src={topic.icon.src}
              alt={topic.icon.alt || topicTitle}
              className="w-8 h-8 object-contain shrink-0"
            />
          )}
          <h1 className="text-2xl font-bold text-gray-800">
            {topicTitle} — Toolbox Tools
          </h1>
        </div>
        <p className="text-sm text-gray-500 mt-1 ml-11">
          Mark lesson pages to show in the learner toolbox under My Tools. Pages
          are listed in section order.
        </p>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <SectionHeader
          icon={
            <WrenchScrewdriverIcon className="w-5 h-5 text-electrum-300 shrink-0" />
          }
          title="Pages"
        />
        {sections.length === 0 ? (
          <p className="text-sm text-gray-400 py-4">
            {topic.lessons?.length
              ? "No pages are available to mark as tools (for example, they may be archived or removed)."
              : "This topic has no sections yet."}
          </p>
        ) : (
          <div className="space-y-6 pt-1">
            <div className="rounded-lg border border-gray-100 px-4 py-3">
              <Checkbox
                label="Check all pages"
                checked={areAllTopicPagesChecked}
                onChange={handleToggleTopicPages}
              />
            </div>
            {sections.map(({ lesson, rows }) => {
              const sectionPageIds = rows.map(({ page }) => page._id);
              const areAllSectionPagesChecked = sectionPageIds.every((pageId) =>
                Boolean(flags[pageId]),
              );
              return (
                <section
                  key={lesson._id}
                  className="rounded-lg border border-gray-100 p-4"
                >
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                    <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {lesson.title}
                    </h2>
                    <Checkbox
                      label="Check section"
                      checked={areAllSectionPagesChecked}
                      onChange={() =>
                        setCheckedStateForPages(
                          sectionPageIds,
                          !areAllSectionPagesChecked,
                        )
                      }
                    />
                  </div>
                  <ul className="space-y-3 m-0 p-0 list-none">
                    {rows.map(({ page, pageTitle }) => (
                      <li key={page._id}>
                        <Checkbox
                          label={pageTitle}
                          checked={Boolean(flags[page._id])}
                          onChange={() => handleToggle(page._id)}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mt-8 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="primary"
            size="sm"
            loading={updateLessonMutation.isPending}
            disabled={!isDirty || updateLessonMutation.isPending}
            onClick={() => void handleSave()}
          >
            Save
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={updateLessonMutation.isPending}
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </div>

      <ConfirmationBox
        isOpen={leaveConfirmOpen}
        title="Unsaved Changes"
        message="You have unsaved tool changes. Do you want to save them before leaving this page?"
        confirmText="Save & Leave"
        cancelText="Discard Changes"
        onConfirm={() => void handleSave(pendingNavigationPath ?? backPath)}
        onCancel={handleDiscardChanges}
        onCloseBox={() => {
          setLeaveConfirmOpen(false);
          setPendingNavigationPath(null);
        }}
      />
    </div>
  );
}
